import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, tokenUtils } from '@coffeeplexity/shared';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  isInitialized: false,
};

// Initialize auth from stored tokens
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const userData = await tokenUtils.getUserData();
      const accessToken = await tokenUtils.getAccessToken();

      if (userData && accessToken && !tokenUtils.isTokenExpired(accessToken)) {
        return userData;
      } else {
        // Token is expired or missing, clear everything
        await tokenUtils.clearAllTokens();
        return null;
      }
    } catch (error: any) {
      await tokenUtils.clearAllTokens();
      return rejectWithValue('Failed to initialize auth');
    }
  }
);

// Refresh current user data from API
export const refreshUser = createAsyncThunk(
  'auth/refreshUser',
  async (_, { rejectWithValue }) => {
    try {
      // Import authApi dynamically to avoid circular dependencies if any
      const { authApi } = require('../../services/api/authApi');
      return await authApi.getCurrentUser();
    } catch (error: any) {
      return rejectWithValue('Failed to refresh user data');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: state => {
      state.user = null;
      state.isAuthenticated = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Initialize Auth
      .addCase(initializeAuth.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.error = action.payload as string;
        state.user = null;
        state.isAuthenticated = false;
      })

      // Refresh User
      .addCase(refreshUser.fulfilled, (state, action) => {
        state.user = action.payload;
        // Also update stored user data
        const { tokenUtils } = require('@coffeeplexity/shared');
        tokenUtils.setUserData(action.payload);
      });
  },
});

export const { clearError, setUser, clearAuth, setLoading, setError } =
  authSlice.actions;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectUserId = (state: { auth: AuthState }) => state.auth.user?.id;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) =>
  state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectIsAuthInitialized = (state: { auth: AuthState }) =>
  state.auth.isInitialized;

export default authSlice.reducer;
